import { Button, ConfirmDialog, DataTable, RowActions, TOUCH } from "@nymbl/nos2-rules";
import { Plus } from "lucide-react";
import { useState } from "react";
import { RuleDialog } from "@/components/calendar/RuleDialog";
import { useCalendarRules, useDeleteRule } from "@/hooks/use-calendar";
import { ruleActionsLabel, ruleColumns } from "@/lib/calendar/rules-view";
import { CALENDAR_RULE_KIND_LABELS } from "@/lib/enums";
import { withCells } from "@/lib/table-columns";
import { formatEntryDate } from "@/lib/time/dates";
import { taskLabel } from "@/lib/time/labels";
import type { CalendarRuleView } from "@/schemas/calendar";

const COLUMNS = ruleColumns();
const NO_RULES: CalendarRuleView[] = [];

const TABLE_COLUMNS = withCells(COLUMNS, {
  matches: {
    className: "max-w-64",
    render: (r) => <span title={r.label}>{r.label}</span>,
  },
  task: {
    className: "min-w-56",
    render: (r) =>
      r.task ? (
        taskLabel(r.task.taskName, r.task.projectName)
      ) : (
        <span className="text-muted-foreground">No longer assigned</span>
      ),
  },
  lastUsed: {
    render: (r) => {
      const day = COLUMNS.find((c) => c.key === "lastUsed")?.accessor(r);
      return typeof day === "string" ? formatEntryDate(day) : "Never";
    },
  },
});

interface RulesTableProps {
  /** `?rule=new`: the Add rule dialog is open. */
  adding: boolean;
  onAddingChange: (open: boolean) => void;
}

/** The person's match rules (table kit), Add rule, and Edit / Delete per row. */
export function RulesTable({ adding, onAddingChange }: RulesTableProps) {
  const rules = useCalendarRules();
  const remove = useDeleteRule();
  const [editing, setEditing] = useState<CalendarRuleView | null>(null);
  const [deleting, setDeleting] = useState<CalendarRuleView | null>(null);
  // A new key per opening so the form starts fresh.
  const [openings, setOpenings] = useState(0);

  const addButton = (
    <Button
      className={TOUCH}
      onClick={() => {
        setOpenings((n) => n + 1);
        onAddingChange(true);
      }}
    >
      <Plus aria-hidden="true" />
      Add rule
    </Button>
  );

  return (
    <div className="min-w-0 space-y-3">
      <DataTable
        label="Calendar rules"
        title="Rules"
        search={{ placeholder: "Search rules…" }}
        exportName="calendar-rules"
        columns={TABLE_COLUMNS}
        rows={rules.data ?? NO_RULES}
        rowKey={(r) => r.id}
        defaultSort="lastUsed"
        defaultSortDir="desc"
        loading={rules.isPending}
        error={rules.error}
        onRetry={() => void rules.refetch()}
        empty={{ title: "No rules yet", action: addButton }}
        controls={addButton}
        rowActions={(r) => (
          <RowActions
            label={ruleActionsLabel(r)}
            actions={[
              { label: "Edit", onSelect: () => setEditing(r) },
              { label: "Delete", onSelect: () => setDeleting(r), destructive: true },
            ]}
          />
        )}
      />

      <RuleDialog key={`new-${openings}`} open={adding} onOpenChange={onAddingChange} rule={null} />
      {editing && (
        <RuleDialog
          key={`edit-${editing.id}`}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          rule={editing}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this rule?"
        description={
          deleting ? `${CALENDAR_RULE_KIND_LABELS[deleting.kind]} “${deleting.label}”` : ""
        }
        hideDescription
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          const rule = deleting;
          if (!rule) return;
          try {
            await remove.mutateAsync(rule.id);
            setDeleting(null);
          } catch {
            // The hook toasted the server's text; the confirm stays open to retry or cancel.
          }
        }}
      />
    </div>
  );
}
